import { Button, Stack, CircularProgress } from "@mui/material";
import { ReactComponent as ApplyIcon } from "../../vendor/_new/icon/certs/apply.svg";
import { ReactComponent as LoadingIcon } from "../../vendor/_new/icon/loading.svg";

import colors from "../../utils/colors";
import LayerdIcon from "./LayerdIcon";
import { UI_ICONS } from "../../icons/uiLucideIcons";

export const SaveButton = ({ onClick, disabled, label = "Save", sx, loading = false }) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled || loading}
      variant="contained"
      disableElevation
      sx={{
        height: 36,
        width: "50%",
        ...containedButtonSx,
        ...sx,
      }}
    >
      {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : label}
    </Button>
  );
};

export const cancelButtonSx = {
  height: 36,
  width: "50%",
  borderRadius: "8px",
  border: `solid 1px ${colors.primary[300]}`,
  fontSize: 14,
  lineHeight: "21px",
  color: colors.primary[300],
  fontWeight: "regular",
  textTransform: "none",
  boxShadow: "none",
  backgroundColor: "transparent",
  "&&:hover": {
    color: colors.primary[400],
    borderColor: colors.primary[400],
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&&:active": {
    color: colors.primary[400],
    borderColor: colors.primary[400],
    backgroundColor: "transparent",
    boxShadow: "none",
  },
  "&&.Mui-disabled": {
    color: colors.neutral[500],
    borderColor: colors.neutral[200],
    backgroundColor: "transparent",
  },
};

export const CancelButton = ({ onClick, label = "Cancel", outlined = false, disabled, sx }) => {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="outlined"
      disableElevation
      sx={{ ...cancelButtonSx, ...sx }}
    >
      {label}
    </Button>
  );
};

export const ApplyButton = ({ onClick, disabled, loading }) => {
  return (
    <Button
      endIcon={
        <Stack alignItems={"center"} justifyContent={"center"} sx={{ width: 24, height: 24 }}>
          {loading ? (
            <LoadingIcon style={{ color: "#fff", width: 14, height: 14 }} />
          ) : (
            <ApplyIcon style={{ color: "#fff" }} />
          )}
        </Stack>
      }
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: 128,
        height: 36,
        ...containedButtonSx,
      }}
    >
      Apply
    </Button>
  );
};

export const RefreshButton = ({ onClick, disabled, loading }) => {
  return (
    <Button
      startIcon={
        <Stack alignItems="center" justifyContent="center" sx={{ width: 26, height: 26 }}>
          {loading ? (
            <LoadingIcon style={{ color: colors.primary[300], width: 16, height: 16 }} />
          ) : (
            <LayerdIcon icon={UI_ICONS.refresh} size="sm" color="primaryLight" />
          )}
        </Stack>
      }
      onClick={onClick}
      disabled={disabled}
      sx={{
        width: 128,
        height: 40,
        ...outlinedButtonSx,
      }}
    >
      Refresh
    </Button>
  );
};

export const containedButtonSx = {
  backgroundColor: colors.primary[300],
  borderRadius: "8px",
  fontSize: 14,
  lineHeight: "21px",
  color: "#fff",
  fontWeight: "regular",
  textTransform: "none",
  border: `1px solid ${colors.primary[300]}`,
  boxShadow: "none",
  "&.Mui-disabled": {
    color: "#fff",
    backgroundColor: colors.primary[200],
    borderColor: colors.primary[200],
    opacity: 0.7,
    cursor: "not-allowed",
  },
  "&:hover": {
    backgroundColor: colors.primary[400],
    borderColor: colors.primary[400],
    color: "#fff",
    boxShadow: "none",
  },
};

/** Drawer footer primary save — 44px height, BM-style disabled opacity. */
export const formSaveButtonSx = {
  height: 44,
  width: "50%",
  ...containedButtonSx,
};

/** Drawer footer cancel — matches formSaveButtonSx height. */
export const formCancelButtonSx = {
  ...cancelButtonSx,
  height: 44,
};

/** V2 toolbar primary — white label/icon, hover darkens to primary[400] (not primary[500]). */
export const primaryButtonSx = {
  ...containedButtonSx,
  height: 36,
  px: 1.75,
  fontSize: 13,
  fontWeight: 500,
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: "inherit",
  },
};

/** V2 toolbar secondary — white fill, neutral border. */
export const ghostButtonSx = {
  height: 36,
  px: 1.75,
  borderRadius: "8px",
  backgroundColor: colors.neutral[100],
  color: colors.neutral[1000],
  border: `1px solid ${colors.neutral[200]}`,
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  lineHeight: "20px",
  boxShadow: "none",
  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
    color: "inherit",
  },
  "&:hover": {
    backgroundColor: colors.neutral[50],
    borderColor: colors.neutral[400],
    color: colors.neutral[1000],
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: colors.neutral[500],
    borderColor: colors.neutral[200],
    backgroundColor: colors.neutral[100],
  },
};

export const ghostButtonSmSx = {
  ...ghostButtonSx,
  height: 30,
  px: 1.25,
  fontSize: 12,
};

export function PrimaryButton({ children, sx, ...rest }) {
  return (
    <Button variant="contained" disableElevation sx={{ ...primaryButtonSx, ...sx }} {...rest}>
      {children}
    </Button>
  );
}

export function GhostButton({ children, sx, ...rest }) {
  return (
    <Button disableElevation sx={{ ...ghostButtonSx, ...sx }} {...rest}>
      {children}
    </Button>
  );
}

export const outlinedButtonSx = {
  borderRadius: "8px",
  fontSize: 14,
  lineHeight: "21px",
  color: colors.primary[300],
  fontWeight: "regular",
  textTransform: "none",
  border: `solid 1px ${colors.primary[300]}`,
  backgroundColor: "transparent",
  "&&:hover": {
    border: `solid 1px ${colors.primary[400]}`,
    color: colors.primary[400],
    backgroundColor: "transparent",
  },
  "&&:active": {
    border: `solid 1px ${colors.primary[400]}`,
    color: colors.primary[400],
    backgroundColor: "transparent",
  },
};
